import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { CamarasMultipleComponent } from '../../../modulos/standalone/camaras-multiple/camaras-multiple.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  FormularioDialogService,
  TamanoFormulario,
} from '../../servicios/formulario-dialog.service';
import { CrearEditarActivoComponent } from '../../../modulos/entidades/activos/activos/crear-editar-activo/crear-editar-activo.component';
import { ILuminaria } from 'modelos/src';
import { LuminariasService } from '../../../modulos/entidades/luminarias/service';
import { alarmasActivas } from '../../../modulos/entidades/luminarias/estado/estado-luminaria';
import { HelperService } from '../../servicios/helper.service';
import { PermisosEntidadesService } from '../../servicios/http/permisos-entidades.service';
import { SOLO_COMANDOS_MANUALES } from '../../../../environments/environment';
import { LoadingComponent } from '../../../componentes/loading/loading.component';
import { CrearEditarLuminariaComponent } from '../../../modulos/entidades/luminarias/luminarias/crear-editar-luminaria/crear-editar-luminaria.component';
import { DialogService } from '../../dialog/dialog.service';
import { TipoDatosPopup } from '../interfaces';
import { SirenasService } from '../../../modulos/entidades/sirenas/service';
import { CrearEditarEmergenciaComponent } from '../../../modulos/entidades/emergencias/emergencias/crear-editar-emergencia/crear-editar-emergencia.component';
import { CrearEditarVehiculoComponent } from '../../../modulos/entidades/vehiculos/crear-editar-vehiculo/crear-editar-vehiculo.component';
import { CrearEditarCentroAtencionComponent } from '../../../modulos/entidades/emergencias/entidades-emergencias/centros-atencion/crear-editar-centro-atencion/crear-editar-centro-atencion.component';
import { CrearEditarHospitalComponent } from '../../../modulos/entidades/emergencias/entidades-emergencias/hospitales/crear-editar-hospital/crear-editar-hospital.component';
import { CrearEditarGatewayComponent } from '../../../modulos/entidades/administracion/gateways/crear-editar-gateway/crear-editar-gateway.component';
import { CrearEditarTrackerComponent } from '../../../modulos/entidades/administracion/trackers/crear-editar-tracker/crear-editar-tracker.component';
import { ParamsService } from '../../servicios/params.service';
import { IEstadoLuminaria } from '../../../modulos/entidades/luminarias/luminarias.types';
import { CrearEditarColectivoComponent } from '../../../modulos/entidades/colectivos/colectivos/crear-editar-colectivo/crear-editar-colectivo.component';
import { MaterialModule } from '../../material.module';
import { TooltipDirective } from '../../tooltip/tooltip.directive';
import { ImageViewerComponent } from '../../image-viewer/image-viewer.component';
import { getUrlFotoVecino } from '../../servicios/vecino-foto.helper';
import {
  IPresentacion,
  estaFinalizada,
  presentacionEstado,
  presentacionPrioridad,
  presentacionSituacion,
  situacionDe,
} from '../../../modulos/entidades/emergencias/estado-emergencia';
import { nivelPerfil } from '../../iconos/iconos';
import { ultimaComunicacionAlarma } from '../../comunicadores-alarma.util';

@Component({
  selector: 'app-popup-generico',
  templateUrl: './popup-generico.component.html',
  styleUrls: ['./popup-generico.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    CamarasMultipleComponent,
    LoadingComponent,
    MaterialModule,
    TooltipDirective,
    ImageViewerComponent,
  ],
})
export class PopupGenericoComponent implements OnChanges {
  dialog = inject(MatDialog);
  private formularios = inject(FormularioDialogService);
  private router = inject(Router);
  luminariasService = inject(LuminariasService);
  helper = inject(HelperService);
  private permisosEntidades = inject(PermisosEntidadesService);
  private dialogService = inject(DialogService);
  sirenasService = inject(SirenasService);
  private cdr = inject(ChangeDetectorRef);
  private paramsService = inject(ParamsService);

  @Input() visible = false;
  @Input() titulo = '';
  @Input() datos: any = null;
  @Input() tipo?: TipoDatosPopup; // Tipo de marker para determinar el template

  @Output() cerrar = new EventEmitter<void>();

  public mostrarInfoAlarma = true; // Información general de la alarma visible por defecto
  public mostrarCamarasAlarma = true; // Cámaras visibles por defecto
  public mostrarVisorFotoVecino = false;

  get fotoVecinoUrl(): string | undefined {
    return getUrlFotoVecino(this.datos?.data?.alerta?.configVecino);
  }

  // Getter para controlar el estado inicial del colapso de sirenas basado en si tiene cámaras
  // Si tiene cámaras, empieza colapsado (true). Si no tiene cámaras, empieza abierto (false)
  get infoSirenaColapsada(): boolean {
    return this._infoSirenaColapsada ?? this.tieneCamarasSirena;
  }

  set infoSirenaColapsada(value: boolean) {
    this._infoSirenaColapsada = value;
  }

  private _infoSirenaColapsada?: boolean;

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar cambios cuando se actualizan los inputs
    if (changes['datos'] || changes['visible']) {
      this.cdr.markForCheck();
    }
    if (changes['datos']) {
      void this.cargarLuminariaCompleta();
      void this.cargarDescripcionesAlarmas();
    }
  }

  /**
   * El mapa de luminarias trae sólo lo que dibuja y cuenta (sin tope, como
   * FARO). Al abrir el popup se pide la luminaria entera (dispositivo con su
   * config, cliente, grupos, modelo, perfil efectivo, comando pendiente);
   * mientras llega se muestra lo que ya hay.
   */
  private luminariaCompleta?: ILuminaria;

  private async cargarLuminariaCompleta(): Promise<void> {
    const id = this.tipo === 'luminaria' ? this.datos?.data?._id : undefined;
    if (!id || this.luminariaCompleta?._id === id) return;
    this.luminariaCompleta = undefined;
    try {
      const completa = await this.luminariasService.luminariaParaPopup(id);
      // El popup pudo haber cambiado de luminaria mientras tanto.
      if (!completa || this.datos?.data?._id !== id) return;
      this.luminariaCompleta = completa;
      this.cdr.markForCheck();
      void this.cargarDescripcionesAlarmas();
    } catch (error) {
      console.error('No se pudo traer la luminaria del popup', error);
    }
  }

  /**
   * Determina si debe mostrar el template genérico
   */
  get esGenerico(): boolean {
    return (
      this.tipo != 'activo' && this.tipo != 'alarma' && this.tipo != 'luminaria'
    );
  }

  /**
   * Obtiene los IDs de cámaras si existen
   */
  get idsCamaras(): string[] | undefined {
    return this.datos?.data?.idsCamaras;
  }

  /**
   * Verifica si tiene cámaras
   */
  get tieneCamaras(): boolean {
    // Sin módulo de cámaras el endpoint /camaras responde 403
    return (
      this.permisosEntidades.verModuloCamaras() &&
      !!this.idsCamaras &&
      this.idsCamaras.length > 0
    );
  }

  /**
   * Obtiene los IDs de cámaras si existen
   */
  get idsAsignados(): string[] | undefined {
    return this.datos?.data?.popupData?.idsAsignados;
  }

  /**
   * Verifica si tiene cámaras
   */
  get tieneCamarasSirena(): boolean {
    return (
      this.permisosEntidades.verModuloCamaras() &&
      !!this.datos?.data?.popupData?.idsAsignados &&
      this.datos?.data?.popupData?.idsAsignados.length > 0
    );
  }

  private parseEstadoConectividad(value: unknown): boolean | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }

    const estado = value.trim().toLowerCase();

    if (
      estado === 'online' ||
      estado === 'activa' ||
      estado === 'encendida' ||
      estado === 'actualizando' ||
      estado === 'en linea' ||
      estado === 'en línea'
    ) {
      return true;
    }

    if (
      estado === 'offline' ||
      estado === 'inactiva' ||
      estado === 'desconectada' ||
      estado === 'error actualización' ||
      estado === 'error actualizacion'
    ) {
      return false;
    }

    return undefined;
  }

  private parseBooleanConectividad(value: unknown): boolean | undefined {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return undefined;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1') return true;
      if (normalized === 'false' || normalized === '0') return false;
    }

    return undefined;
  }

  get conectividadSirena(): boolean | undefined {
    const sirena = this.datos?.data?.popupData?.sirena;

    const estados = [
      sirena?.estado,
      this.datos?.data?.popupData?.estado,
      this.datos?.data?.estado,
    ];

    for (const estado of estados) {
      const parsed = this.parseEstadoConectividad(estado);
      if (parsed !== undefined) {
        return parsed;
      }
    }

    const onlineValues = [
      sirena?.online,
      this.datos?.data?.popupData?.online,
      this.datos?.data?.online,
    ];

    for (const online of onlineValues) {
      const parsed = this.parseBooleanConectividad(online);
      if (parsed !== undefined) {
        return parsed;
      }
    }

    return undefined;
  }

  get fechaComunicacionSirena(): string | number | Date | null {
    const sirena = this.datos?.data?.popupData?.sirena;
    const conectada = this.conectividadSirena;

    if (conectada === true) {
      return sirena?.fechaOnline || sirena?.fechaOffline || null;
    }

    if (conectada === false) {
      return sirena?.fechaOffline || sirena?.fechaOnline || null;
    }

    return sirena?.fechaOnline || sirena?.fechaOffline || null;
  }

  //
  //
  // LUMINARIAS
  //
  //
  // Descripción por código de la tabla del modelo LoRaWAN de la luminaria
  // abierta. Se carga aparte (ver cargarDescripcionesAlarmas).
  private descripcionesAlarmas = new Map<string, string>();
  private idCodigosCargados?: string;

  // Alarmas activas (las mismas que la ponen en E3), con su descripción si el
  // modelo la tiene; si no, el código tal cual.
  get alarmas(): string[] {
    if (this.tipo !== 'luminaria' || !this.luminaria) return [];
    return alarmasActivas(this.luminaria).map(
      (codigo) => this.descripcionesAlarmas.get(codigo) ?? codigo,
    );
  }

  private async cargarDescripcionesAlarmas(): Promise<void> {
    if (this.tipo !== 'luminaria' || !this.luminaria) return;
    const idCodigos = (
      this.luminaria.dispositivo?.modeloDispositivo as
        | { idCodigos?: string }
        | undefined
    )?.idCodigos;
    if (!idCodigos || idCodigos === this.idCodigosCargados) return;
    if (!alarmasActivas(this.luminaria).length) return;

    this.idCodigosCargados = idCodigos;
    const descripciones =
      await this.luminariasService.descripcionesCodigos(idCodigos);
    // El popup pudo haber cambiado de luminaria mientras tanto.
    if (this.idCodigosCargados !== idCodigos) return;
    this.descripcionesAlarmas = descripciones;
    this.cdr.markForCheck();
  }

  get comunicacionCalculada(): any {
    return this.luminariasService.calcularEstadoComunicacion(this.luminaria);
  }

  get getEstado(): IEstadoLuminaria {
    return this.luminariasService.determinarEstadoLuminaria(this.luminaria);
  }

  get getTurnOnOffCalculado(): boolean | null {
    return this.luminariasService.isEncendida(this.luminaria);
  }

  get luminaria(): ILuminaria {
    const resumen = this.datos?.data as ILuminaria;
    return this.luminariaCompleta?._id === resumen?._id
      ? this.luminariaCompleta!
      : resumen;
  }

  //
  // Comandos individuales de luminaria (misma lógica/restricciones que el listado)
  //

  // Nivel del perfil efectivo / asignaciones, igual que el listado.
  public nivelMeta(nivel: string): { label: string; icon: string } {
    return nivelPerfil(nivel);
  }

  // Perfil efectivo y asignaciones (computados en backend con incluirPerfilEfectivo)
  public get perfilEfectivo(): any {
    return (this.luminaria as any)?.perfilEfectivo || null;
  }

  public get asignaciones(): any[] {
    return (this.luminaria as any)?.asignaciones || [];
  }

  // Tipo de dispositivo (para ocultar comandos no aplicables)
  public get esWellness(): boolean {
    return this.luminaria?.dispositivo?.tipo === 'Luminaria Wellness';
  }
  public get esActis(): boolean {
    return this.luminaria?.dispositivo?.tipo === 'Luminaria ACTIS FING';
  }
  public get esGPE(): boolean {
    return this.luminaria?.dispositivo?.tipo === 'Luminaria GPE';
  }

  // Si está activo, en el popup del mapa solo se ofrecen algunos comandos manuales
  public get soloComandosManuales(): boolean {
    return SOLO_COMANDOS_MANUALES;
  }

  // Si tiene perfil efectivo, se ocultan comandos que serían sobrescritos por el perfil
  public get tienePerfilEfectivo(): boolean {
    return !!this.luminaria?.perfilEfectivo;
  }

  // ACTIS con perfiles de dimming disponibles
  public get dimmingDisponible(): boolean {
    return (
      this.esActis &&
      this.luminariasService.perfilTienePerfilesDimming(
        this.luminaria?.perfilConfig,
      )
    );
  }

  // Restricción común de todos los comandos: comando pendiente o sin permiso
  public get comandoDeshabilitado(): boolean {
    return (
      !!(this.luminaria as any)?.tieneComandoPendiente ||
      !this.helper.tienePermiso('Luminarias - Enviar comandos')
    );
  }

  //
  //
  // Emergencias Médicas
  //
  //
  // Paleta única de estado-emergencia.ts (la misma que listados, tablero y mapa)
  public get estadoEmergencia(): IPresentacion {
    return presentacionEstado(this.datos?.data?.ultimoEventoEmergencia?.estado);
  }

  public get prioridadEmergencia(): IPresentacion | null {
    const prioridad =
      this.datos?.data?.prioridadCierre || this.datos?.data?.prioridadApertura;
    return prioridad ? presentacionPrioridad(prioridad) : null;
  }

  public get situacionEmergencia(): IPresentacion {
    return presentacionSituacion(situacionDe(this.datos?.data));
  }

  // El evento puede traer el móvil populado en detallesEmergencias o en activo
  public get movilEmergencia(): string | undefined {
    const evento = this.datos?.data?.ultimoEventoEmergencia;
    return (
      evento?.detallesEmergencias?.movilAsignado?.identificacion ||
      evento?.activo?.identificacion ||
      undefined
    );
  }

  public get sintomasEmergencia(): string[] {
    return this.datos?.data?.emergenciaMedica?.sintomasApertura || [];
  }

  // Editar desde el popup: con permiso y mientras no esté cerrada
  public get puedeEditar(): boolean {
    switch (this.tipo) {
      case 'emergenciaMedica':
        return (
          this.helper.tienePermiso('Emergencias Médicas - Editar') &&
          !estaFinalizada(this.datos?.data)
        );
      case 'hospital':
        return this.helper.tienePermiso(
          'Emergencias Médicas - Editar hospitales',
        );
      case 'centroMedico':
        return this.helper.tienePermiso(
          'Emergencias Médicas - Editar centros médicos',
        );
      case 'alertaSeguridad':
      case 'eventoSeguridad':
        return false;
      default:
        return true;
    }
  }

  public get velocidad(): number | undefined {
    if (!this.datos?.data?.ultimoReporte?.tipoReporte) return undefined;
    const tipo = this.datos.data.ultimoReporte.tipoReporte;

    if (
      tipo === 'Tracker 4G' ||
      tipo === 'Tracker Qualcomm' ||
      tipo === 'Tracker T1000B'
    ) {
      return (this.datos.data.ultimoReporte as any).velocidad;
    }

    return undefined;
  }

  /** Alarma: la comunicación más reciente de sus comunicadores (el
   *  fechaUltimaComunicacion de la alarma ya no se actualiza). */
  public ultimaComunicacionAlarma(): string | undefined {
    return ultimaComunicacionAlarma(this.datos?.data);
  }

  public fechaUltimaComunicacionAmbulancia(): string {
    if (!this.datos?.data?.ultimoReporte?.fechaCreacion) return null;

    const fecha = new Date(this.datos?.data?.ultimoReporte?.fechaCreacion);
    const color = this.datos?.data?.color;

    // Mapeamos el color a un ícono
    const colorIconoMap: Record<string, string> = {
      green:
        '<span class="material-icons" style="font-size: 12px; color: #4caf50; vertical-align: middle;">circle</span>',
      yellow:
        '<span class="material-icons" style="font-size: 12px; color: #fbc02d; vertical-align: middle;">circle</span>',
      red: '<span class="material-icons" style="font-size: 12px; color: #f44336; vertical-align: middle;">circle</span>',
      black:
        '<span class="material-icons" style="font-size: 12px; color: #616161; vertical-align: middle;">circle</span>',
    };

    const icono =
      colorIconoMap[color] ||
      '<span class="material-icons" style="font-size: 12px; color: #616161; vertical-align: middle;">circle</span>';

    // Formateamos la fecha
    const fechaFormateada = HelperService.parseDate(fecha);

    // Armamos el string final
    const resultado = `${icono} ${fechaFormateada}`;

    return resultado;
  }

  public getColorTransparente(hex: string, alpha: number = 0.2): string {
    if (!hex) return 'rgba(0,0,0,0.1)';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  //
  //
  //

  public frecReporteTexto(luminaria: ILuminaria | null | undefined): string {
    const config = luminaria?.dispositivo?.config as any;
    let value;
    if (luminaria?.dispositivo?.tipo === 'Luminaria GPE') {
      // Si frecReporte puede ser number o string, normalizamos:
      value = config?.frecReporte ?? config?.['frecReporte'];
      if (value === null || value === undefined) {
        return '';
      }
      // Si es string vacío -> consideramos "sin"
      if (typeof value === 'string' && value.trim() === '') {
        return '';
      }
    }
    if (luminaria?.dispositivo?.tipo === 'Luminaria Wellness') {
      // Si frecReporte puede ser number o string, normalizamos:
      value = 15;
    }
    if (luminaria?.dispositivo?.tipo === 'Luminaria ACTIS FING') {
      // Si frecReporte puede ser number o string, normalizamos:
      value = config?.frecReportePeriodico || 15;
    }

    // Devolvemos string (si es number lo convertimos); sin dato, ''
    return value === null || value === undefined ? '' : String(value);
  }

  /**
   * Formatea el valor para mostrarlo
   */
  public formatearValor(valor: any): string {
    if (typeof valor === 'object' && valor !== null) {
      return JSON.stringify(valor, null, 2);
    }
    return String(valor);
  }

  /**
   * Emite el evento para cerrar el popup
   */
  public onCerrar(): void {
    this.cerrar.emit();
  }

  /**
   * Navega a la vista de detalles
   */
  public alertaActiva(): boolean {
    if (this.tipo !== 'alertaSeguridad') return false;
    const estado =
      this.datos?.data?.alerta?.estadoActual || this.datos?.data?.estadoActual;
    if (!estado) return false;
    return estado !== 'Finalizada' && estado !== 'Derivada Finalizada';
  }

  public verDetalles(): void {
    switch (this.tipo) {
      case 'alarma':
        this.router.navigate([
          'main/alarmas/alarmasDomiciliarias/detallesAlarma',
          this.datos.data._id,
        ]);
        break;
      case 'activo':
        this.router.navigate([
          'main/activos/activos/detalles',
          this.datos?.data?.activo?._id,
        ]);
        break;
      case 'luminaria':
        this.router.navigate([
          'main/luminarias/luminarias/detalles',
          this.datos?.data?._id,
        ]);
        break;
      case 'gateway':
        this.router.navigate([
          'main/administracion/gateways/detalles',
          this.datos?.data?._id,
        ]);
        break;
      case 'tracker':
        this.router.navigate([
          'main/administracion/trackers/detallesTracker',
          this.datos?.data?.tracker?._id,
        ]);
        break;
      case 'vehiculo':
        this.router.navigate([
          'main/vehiculos/vehiculos/detalles',
          this.datos?.data?.activo?._id,
        ]);
        break;
      case 'colectivo':
        this.router.navigate([
          '/main/colectivos/colectivos/detalles',
          this.datos?.data?.activo?._id,
        ]);
        break;

      case 'sirena':
        {
          const idSirena =
            this.datos?.data?.idExterno ||
            this.datos?.data?.popupData?.idExterno;
          const idCliente =
            this.datos?.data?.idCliente ||
            this.datos?.data?.popupData?.idCliente ||
            this.datos?.data?.sirena?.idCliente ||
            this.datos?.data?.popupData?.sirena?.idCliente;

          if (!idSirena) {
            break;
          }

          // El detalle de la integración es una página (antes un diálogo)
          this.router.navigate(['/main/sirenas/sirenas/detalles', idSirena], {
            queryParams: idCliente ? { cliente: idCliente } : {},
          });
        }
        break;
      case 'emergenciaMedica':
        this.router.navigate([
          'main/emergencias/emergenciasMedicas/detalles',
          this.datos?.data?._id,
        ]);
        break;
      case 'hospital':
        this.router.navigate([
          'main/emergencias/hospitales/detalles',
          this.datos?.data?._id,
        ]);
        break;

      case 'centroMedico':
        this.router.navigate([
          'main/emergencias/centrosAtencion/medica/detalles',
          this.datos?.data?._id,
        ]);
        break;
      case 'ambulancia':
        this.router.navigate([
          'main/vehiculos/vehiculos/detalles',
          this.datos?.data?._id,
        ]);
        break;
      case 'alertaSeguridad': {
        const idAlerta = this.datos?.data?.alerta?._id || this.datos?.data?._id;
        if (!idAlerta) break;
        // Dynamic import para romper círculo:
        // popup-generico → Tratar → MapaFacilV2Component → popup-generico
        import('../../../modulos/entidades/alertas-seguridad/tratar-alerta-seguridad/tratar-alerta-seguridad.component').then(
          (mod) => {
            this.dialog.open(mod.TratarAlertaSeguridadComponent, {
              data: { idAlerta },
              width: '1080px',
              maxWidth: '96vw',
              maxHeight: '95vh',
              disableClose: true,
              panelClass: 'alerta-tratamiento-dialog',
            });
          },
        );
        break;
      }
    }
  }

  /**
   * Navega a la vista de edición
   */
  public editar(): void {
    switch (this.tipo) {
      case 'alarma':
        if (this.datos?.data?._id) {
          this.router.navigate([
            'main/alarmas/alarmasDomiciliarias/editar',
            this.datos.data._id,
          ]);
          this.onCerrar();
        }
        break;
      case 'activo':
        if (this.datos?.data?.activo?._id) {
          this.formularios.abrir(CrearEditarActivoComponent, {
            tamano: 'mediano',
            data: {
              edit: this.datos.data.activo,
              tipo: 'Normal',
            },
          });
        }
        break;
      case 'luminaria':
        this.abrirDialogEdicion(
          CrearEditarLuminariaComponent,
          { edit: this.datos.data },
          'mediano',
        );
        break;
      case 'tracker':
        if (this.datos?.data?.tracker) {
          this.paramsService.setParams({ tracker: this.datos.data.tracker });
          this.formularios.abrir(CrearEditarTrackerComponent, {
            tamano: 'grande',
          });
        }
        break;
      case 'gateway':
        this.abrirDialogEdicion(
          CrearEditarGatewayComponent,
          { edit: this.datos.data },
          'mediano',
        );
        break;
      case 'vehiculo':
        this.paramsService.setParams({
          vehiculo: this.datos.data?.activo,
        });
        this.abrirDialogEdicion(
          CrearEditarVehiculoComponent,
          undefined,
          'grande',
        );
        break;
      case 'colectivo':
        this.router.navigate([
          'main/colectivos/colectivos/editar',
          this.datos.data.activo._id,
        ]);
        break;
      case 'sirena':
        if (this.datos?.data?._id) {
          this.router.navigate([
            'main/sirenas/sirenas/editar',
            this.datos.data._id,
          ]);
          this.onCerrar();
        }
        break;
      case 'emergenciaMedica':
        this.abrirDialogFormulario(
          CrearEditarEmergenciaComponent,
          { edit: this.datos.data },
          'grande',
        );
        break;
      case 'hospital':
        this.abrirDialogFormulario(
          CrearEditarHospitalComponent,
          { edit: this.datos.data },
          'mediano',
        );
        break;
      case 'ambulancia':
        this.abrirDialogEdicion(
          CrearEditarVehiculoComponent,
          { vehiculo: this.datos.data },
          'grande',
        );
        break;
      case 'centroMedico':
        this.abrirDialogFormulario(
          CrearEditarCentroAtencionComponent,
          { id: this.datos.data._id },
          'mediano',
        );
        break;
    }
  }

  /**
   * Formularios de Emergencias Médicas: tamaño fijo del servicio de
   * formularios. Actualiza los datos al cerrar.
   */
  private abrirDialogFormulario(
    component: any,
    data: any,
    tamano: TamanoFormulario,
  ): void {
    const dialogRef = this.formularios.abrir(component, { tamano, data });
    dialogRef.afterClosed().subscribe((resultado: any) => {
      if (resultado) {
        this.datos.data = resultado;
      }
    });
  }

  /**
   * Abre un dialog de edición y actualiza los datos al cerrar
   */
  private abrirDialogEdicion(
    component: any,
    data: any,
    tamano: TamanoFormulario = 'mediano',
  ): void {
    const dialogRef = this.formularios.abrir(component, { tamano, data });

    dialogRef.afterClosed().subscribe((resultado: any) => {
      if (resultado) {
        this.datos.data = resultado;
      }
    });
  }

  /**
   * Maneja el evento de tratar el último evento de la alarma
   */
  public async tratarEvento(evento: any): Promise<void> {
    const dialogs = this.dialog.openDialogs;
    const idAbierto = dialogs.find(
      (d) => d.componentInstance.data._id === evento._id,
    );
    if (!idAbierto) {
      try {
        const config: MatDialogConfig = {
          data: { idEvento: evento._id },
          hasBackdrop: false,
          minWidth: '800px',
          maxWidth: '1000px',
          closeOnNavigation: false,
          disableClose: true,
        };
        const { TratamientoGenericoComponent } =
          await import('../../../modulos/entidades/eventos-genericos/tratar-evento-generico/tratamiento-generico/tratamiento-generico.component');
        const dialog = this.dialog.open(TratamientoGenericoComponent, config);
        dialog.afterClosed().subscribe(() => {
          // Aquí podrías recargar datos si fuera necesario
        });
      } catch (error) {
        console.error('Error al obtener evento completo:', error);
        this.helper.notifError('Error al cargar el evento');
      }
    }
  }

  public async eventoTecnicoPopup(idEvento: string) {
    try {
      const { TratamientoGenericoComponent } =
        await import('../../../modulos/entidades/eventos-genericos/tratar-evento-generico/tratamiento-generico/tratamiento-generico.component');
      const config: MatDialogConfig = {
        data: { idEvento },
        hasBackdrop: false,
        minWidth: '800px',
        maxWidth: '1000px',
        closeOnNavigation: false,
        disableClose: true,
      };
      const dialog = this.dialog.open(TratamientoGenericoComponent, config);
      dialog.afterClosed().subscribe(() => {
        // Aquí podrías recargar datos si fuera necesario
      });
    } catch (error) {
      console.error('Error al obtener evento técnico completo:', error);
      this.helper.notifError('Error al cargar el evento técnico');
    }
  }
}
